import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import * as Sentry from '@sentry/react'
import {
  ComponentInteractionRequest,
  ComponentList,
  type LLMImplementations,
  OperationsModelImplementations,
  ValuesRequest,
  ValuesResponse,
  Game,
  Component,
  ComponentItem,
} from '@/types/models/v2'
import * as otel from '@/lib/otel'
const REQUEST_ID_HEADER = 'x-request-id'
const CONVERSATION_ID_HEADER = 'x-conversation-id'

const PRD_API_URL = 'https://edk-v1.prd.uglabs.app/api'
const DEV_API_URL = 'https://edk-v1.dev.uglabs.app/api'
const APPLAUSE_API_URL = 'https://edk-v1-applause.prd.uglabs.app/api'

// Environment configurations
const ENV_CONFIGS = {
  prd: {
    apiUrl: PRD_API_URL,
    clientId: 'svc-public.galbrachapublic.default-dk7m',
    clientSecret: '4foB3qe8IL-7lf_eNu8GXHqVWuVaIe94rnxilAtU63A',
    hostnamePattern: (hostname: string) => hostname.includes('demos.uglabs'),
  },
  dev: {
    apiUrl: DEV_API_URL,
    clientId: 'svc-public.galbrachadev.default-fh8z',
    clientSecret: 'ZUKRVurrki9sLDcmN6RA_xY-0FOhRwfQMMOFBI0YBOg',
    hostnamePattern: (hostname: string) => hostname.includes('demos.dev.uglabs'),
  },
  applause: {
    apiUrl: APPLAUSE_API_URL,
    clientId: 'svc-ug.webdemos.default-t3ik',
    clientSecret: '1TkA-nKx9pFjI-VblpNtnyZKtI7d4_jsKaEQWcI_2vg',
    hostnamePattern: (hostname: string) => hostname.includes('demos.applause.uglabs'),
  },
} as const

class Api {
  private _authToken?: string
  private _userAuthToken?: string
  private _clientId?: string
  private _clientSecret?: string
  private _requestId: string | null = null // Stores the latest X-Request-ID
  private _traceId: string | null = null // Stores the latest trace-id
  apiUrl: string
  apiClient: AxiosInstance

  constructor(apiUrl?: string) {
    // If user provides environment variables, use those instead of PRD/DEV logic
    const userApiUrl = process.env.REACT_APP_API_URL
    const userClientId = process.env.REACT_APP_CLIENT_ID
    const userClientSecret = process.env.REACT_APP_CLIENT_SECRET

    // Debug log to verify environment variables
    console.log('Environment Variables:', {
      API_URL: process.env.REACT_APP_API_URL,
      CLIENT_ID: process.env.REACT_APP_CLIENT_ID,
      CLIENT_SECRET: process.env.REACT_APP_CLIENT_SECRET ? '***' : undefined,
    })

    const isEnvVariablesSet = userApiUrl && userClientId && userClientSecret
    if (isEnvVariablesSet) {
      this.apiUrl = userApiUrl
      this._clientId = userClientId
      this._clientSecret = userClientSecret
    } else {
      const hostname = window.location.hostname
      const envConfig =
        Object.values(ENV_CONFIGS).find((config) => config.hostnamePattern(hostname)) ||
        ENV_CONFIGS.dev

      this.apiUrl = apiUrl || envConfig.apiUrl
      this._clientId = envConfig.clientId
      this._clientSecret = envConfig.clientSecret
    }

    this._authToken = localStorage.getItem('authToken') || undefined
    this._userAuthToken = localStorage.getItem('userAuthToken') || undefined

    this.apiClient = axios.create({
      baseURL: this.apiUrl,
      headers: {
        Authorization: `Bearer ${this.authToken}`,
      },
    })
    this.setInterceptors()
  }

  private setInterceptors() {
    // Add authentication check interceptor
    this.apiClient.interceptors.request.use(
      async (config) => {
        // Skip auth check for loginWithClient to prevent stack overflow
        if (config.url?.includes('/auth/client')) {
          return config
        }

        const isAuthenticated = Boolean(localStorage.getItem('authToken') || this.authToken)
        if (!isAuthenticated) {
          try {
            await this.loginWithClient(this.clientId || '', this.clientSecret || '')
          } catch (error) {
            console.error('Failed to authenticate with client credentials:', error)
            // Clear any invalid tokens
            localStorage.removeItem('authToken')
            localStorage.removeItem('userAuthToken')
            this._authToken = undefined
            this._userAuthToken = undefined
          }
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Add request interceptor to set traceparent
    this.apiClient.interceptors.request.use(
      (config) => {
        const [traceParent, traceId] = otel.generateTraceParent()
        this._traceId = traceId
        config.headers[otel.TRACEPARENT_HEADER] = traceParent
        return config
      },
      (error) => Promise.reject(error)
    )

    this.apiClient.interceptors.response.use(
      (response: AxiosResponse) => {
        this.updatedRequestFromResponse(response)
        return response
      },
      (error: any) => {
        if (error.response) {
          this.updatedRequestFromResponse(error.response)
        }
        return Promise.reject(error)
      }
    )

    // Original interceptor that calls handleError on error responses
    this.apiClient.interceptors.response.use((response) => response, this.handleError.bind(this))

    // Original interceptor that logs errors to Sentry with the response body
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          try {
            const body = JSON.stringify(error.response.data)
            Sentry.captureException(error, {
              extra: {
                url: error.config.url,
                status: error.response.status,
                response_body: body,
              },
            })

            // Clear auth token if we get a 401 response
            if (axios.isAxiosError(error) && error.response?.status === 401) {
              localStorage.removeItem('authToken')
              localStorage.removeItem('userAuthToken')
              this._authToken = undefined
              this._userAuthToken = undefined
            }

            return Promise.reject(error)
          } catch (loggingError) {
            console.error(loggingError)
            return Promise.reject(loggingError)
          }
        }
        Sentry.captureException(error)
        return Promise.reject(error)
      }
    )
  }

  private updatedRequestFromResponse(response: AxiosResponse) {
    this.updateRequestId(response.headers[REQUEST_ID_HEADER] ?? '')
    return response
  }

  private handleError(error: AxiosError) {
    return Promise.reject(error)
  }

  public get authToken(): string | undefined {
    return this._authToken
  }

  public set authToken(newAuthToken: string | undefined) {
    localStorage.setItem('authToken', newAuthToken || '')
    this._authToken = newAuthToken
    this.apiClient.defaults.headers['Authorization'] = `Bearer ${newAuthToken}`
  }

  // unlike auth token - this allows us to impersonate to another user
  public set userAuthToken(newAuthToken: string | undefined) {
    localStorage.setItem('userAuthToken', newAuthToken || '')
    this._userAuthToken = newAuthToken
  }

  public get userAuthToken(): string | undefined {
    return this._userAuthToken
  }

  public set clientSecret(newSecret: string | undefined) {
    localStorage.setItem('clientSecret', newSecret || '')
    this._clientSecret = newSecret
  }

  public set clientId(newClientId: string | undefined) {
    localStorage.setItem('clientId', newClientId || '')
    this._clientId = newClientId
  }

  public get clientId(): string | undefined {
    return this._clientId
  }

  public get clientSecret(): string | undefined {
    return this._clientSecret
  }

  public get requestId(): string | null {
    return this._requestId
  }

  public get traceId(): string | null {
    return this._traceId
  }

  private updateRequestId(newRequestId: string) {
    if (newRequestId) {
      this._requestId = newRequestId
    }
  }

  async login(organization_id: string, email: string, password: string): Promise<void> {
    const response = await this.apiClient.post(`/auth/login`, {
      organization_id,
      email,
      password,
    })

    this.authToken = response.data.auth_token
  }

  async loginWithJWT(token: string): Promise<void> {
    const response = await this.apiClient.post(`/auth/login-jwt`, { token })
    this.authToken = response.data.auth_token
  }

  async setAuthClientSecretId(): Promise<void> {
    if (this.clientSecret && this.clientId) {
      console.debug('Client secret and id already set')
      return
    }
    const response = await this.apiClient.get('/auth/client')
    this.clientId = response.data.client_id
    this.clientSecret = response.data.client_secret
  }

  async loginWithClient(
    clientId: string | null = null,
    clientSecret: string | null = null,
    userId: string = 'web-demos'
  ): Promise<string> {
    if (clientId) {
      this.clientId = clientId
    }
    if (clientSecret) {
      this.clientSecret = clientSecret
    }
    const response = await this.apiClient.post('/auth/client', {
      client_id: clientId || this.clientId,
      client_secret: clientSecret || this.clientSecret,
      user_id: userId,
    })
    this.authToken = response.data.auth_token
    this.apiClient.defaults.headers.common['Authorization'] = `Bearer ${this.authToken}`
    return this.authToken || ''
  }

  /**
   * Stream data from url, and handle line-by-line JSON responses.
   */
  async streamJsonLineData(
    url: string,
    requestBody: any,
    requestHeaders: any | null,
    onStreamData: (data: any[]) => void,
    onStartedNewRequest: (requestId: string) => void
  ): Promise<string> {
    try {
      // Use fetch for streaming - as axios does not support streaming

      const [traceParent, traceId] = otel.generateTraceParent()
      this._traceId = traceId
      const headers: any = {
        'Content-Type': 'application/json',
        [otel.TRACEPARENT_HEADER]: traceParent,
        ...requestHeaders,
      }

      const authToken = this._userAuthToken || this.authToken
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: requestBody,
      })

      if (!response.ok) {
        throw new Error(`Server error. Status: ${response.status}`)
      }

      const requestId = response.headers.get(REQUEST_ID_HEADER) || ''
      this.updateRequestId(requestId)
      if (onStartedNewRequest) {
        onStartedNewRequest(requestId)
      }

      const reader = response.body!.getReader()
      const decoder = new TextDecoder()
      let done = false
      let jsonString = ''

      while (!done) {
        const { value, done: streamDone } = await reader.read()
        done = streamDone

        if (value) {
          jsonString += decoder.decode(value, { stream: true })

          const jsonLines = jsonString.split('\n')
          jsonString = jsonLines.pop() || ''

          const newData = jsonLines.map((singleLine) => JSON.parse(singleLine))
          onStreamData(newData)
        }
      }
      return response.headers.get(CONVERSATION_ID_HEADER) || ''
    } catch (error) {
      console.error('Error during streamData:', error)
      throw error
    }
  }

  async getComponent(componentId: string): Promise<Component> {
    const response: AxiosResponse<ComponentItem> = await this.apiClient.get(
      `/components/${componentId}`
    )
    return response.data.component
  }

  async getGames(): Promise<{ games: Game[] }> {
    const response = await this.apiClient.get(`/games`)
    return response.data
  }

  async getGame(gameId: string): Promise<{ game: Game }> {
    const response = await this.apiClient.get(`/games/${gameId}`)
    return response.data
  }

  // we default to auth token if user auth token is not defined
  getUserAuthHeaders() {
    return {
      Authorization: `Bearer ${this._userAuthToken || this.authToken}`,
    }
  }

  async getAllComponents({
    gameId,
    query,
    scope,
    playable,
  }: {
    gameId?: string
    query?: string
    scope?: 'game' | 'organization' | 'builtin'
    playable?: boolean
  }): Promise<ComponentList> {
    const response = await this.apiClient.get(`/components`, {
      params: {
        game_id: gameId,
        query: query,
        scope: scope,
        playable: playable,
      },
    })
    return response.data
  }

  public async getConversationValues(
    componentId: string,
    conversationId: string
  ): Promise<AxiosResponse<ValuesResponse>> {
    return this.apiClient.get(`/components/${componentId}/conversations/${conversationId}/values`)
  }

  public async setConversationValues(
    componentId: string,
    conversationId: string,
    requestBody: ValuesRequest
  ): Promise<AxiosResponse<ValuesResponse>> {
    return this.apiClient.post(
      `/components/${componentId}/conversations/${conversationId}/values`,
      requestBody
    )
  }

  public async downloadBinary(name: string): Promise<AxiosResponse> {
    return this.apiClient.get(`/binary/${name}`, { responseType: 'arraybuffer' })
  }

  public async uploadBinary(dataURI: string): Promise<AxiosResponse> {
    return this.apiClient.post(`/binary`, { data_url: dataURI })
  }

  public async interact(
    componentId: string,
    request: ComponentInteractionRequest
  ): Promise<AxiosResponse> {
    return this.apiClient.post(`/components/${componentId}/interact`, request)
  }

  public async operationsGenerateImage(): Promise<OperationsModelImplementations> {
    const response = await this.apiClient.get('/operations/generate-image')
    return response.data
  }

  public async getOperationsLLM(): Promise<LLMImplementations> {
    const response = await this.apiClient.get('/operations/llm')
    return response.data
  }
}

export default Api
